import type { Spread } from "lexical";
import {
	DOMConversionMap,
	DOMConversionOutput,
	DOMExportOutput,
	EditorConfig,
	NodeKey,
	SerializedTextNode,
	TextNode,
} from "lexical";

import css from "./HashtagNodeStyles.module.scss";

export type SerializedHashtagNode = Spread<
	{
		hashtagName: string;
		type: "hashtag";
		version: 1;
	},
	SerializedTextNode
>;

function convertHashtagElement(domNode: HTMLElement): DOMConversionOutput | null {
	const textContent = domNode.textContent;

	if (textContent !== null) {
		const node = $createHashtagNode(textContent);
		return {
			node,
		};
	}

	return null;
}

// Inherit and modify from TextNode provided by Lexical
// https://lexical.dev/docs/api/classes/lexical.TextNode
export class HashtagNode extends TextNode {
	__hashtag: string;

	static getType(): string {
		return "hashtag";
	}

	static clone(node: HashtagNode): HashtagNode {
		return new HashtagNode(node.__hashtag, node.__text, node.__key);
	}

	constructor(hashtagName: string, text?: string, key?: NodeKey) {
		super(text ?? hashtagName, key);
		this.__hashtag = hashtagName;
	}

	// Override the createDOM method to add a class name
	createDOM(config: EditorConfig): HTMLElement {
		const dom = super.createDOM(config);
		dom.className = css.hashtag;
		return dom;
	}

	// Override the exportDOM method to add a data attribute
	// Controls how the hashtag is exported to HTML.
	// Needed primarily for a smooth copy/paste experience.
	exportDOM(): DOMExportOutput {
		const element = document.createElement("span");
		element.setAttribute("data-lexical-hashtag", "true");
		element.textContent = this.__text;
		return { element };
	}

	// Override the importDOM method to convert the hashtag element
	// to a HashtagNode.
	static importDOM(): DOMConversionMap | null {
		return {
			span: (domNode: HTMLElement) => {
				if (!domNode.hasAttribute("data-lexical-hashtag")) {
					return null;
				}
				return {
					conversion: convertHashtagElement,
					priority: 1,
				};
			},
		};
	}

	// Ensure JSON and default HTML serialization create our HashtagNode
	static importJSON(serializedNode: SerializedHashtagNode): HashtagNode {
		const node = $createHashtagNode(serializedNode.hashtagName);
		node.setTextContent(serializedNode.text);
		node.setFormat(serializedNode.format);
		node.setDetail(serializedNode.detail);
		node.setMode(serializedNode.mode);
		node.setStyle(serializedNode.style);
		return node;
	}

	// Ensure JSON and default HTML serialization create our HashtagNode
	exportJSON(): SerializedHashtagNode {
		return {
			...super.exportJSON(),
			hashtagName: this.__hashtag,
			type: "hashtag",
			version: 1,
		};
	}

	isSegmented(): false {
		return false;
	}

	isTextEntity(): true {
		return true;
	}

	isToken(): true {
		return true;
	}
}

export function $createHashtagNode(hashtagName: string): HashtagNode {
	const hashtagNode = new HashtagNode(hashtagName);
	hashtagNode.setMode("segmented").toggleDirectionless();
	return hashtagNode;
}
